// GeoJSON converters and MapLibre width-law expressions, bridging the SVG-era
// geometry helpers (mapBorders.ts, mapLines.ts, polygonUnion.ts) to the
// MapLibre GL layers in @wehunt/map. Pure functions only — no React, no I/O.

import {
  GROUND_HALO_RATIO,
  GROUND_HIT_TARGET_CSS_PX,
  GROUND_MIN_EFFECTIVE_ZOOM,
  groundShapes,
  groundStrokeCssPx,
  resolveHuntBorderColor,
  type GroundLayer,
} from './mapBorders'
import { ringsToPolygons } from './polygonUnion'
import {
  LINE_DEFAULT_COLOR,
  LINE_DEFAULT_WIDTH,
  LINE_MAX_WIDTH,
  LINE_MIN_WIDTH,
  MAPLIBRE_LAW_ZOOM_OFFSET,
  lineStrokeWidthCssPx,
} from './mapLines'
import { mercY } from './geo'
import type { GeoArea, GeoLine, GeoLineStyle, GeoPt } from './types'
import { MAP_MAX_ZOOM, MAP_MIN_ZOOM } from '@wehunt/map'

/** [lon, lat] — GeoJSON coordinate order, the reverse of this codebase's GeoPt. */
type GeoJsonPos = [number, number]

// A minimal structural shape, checked against maplibre's `addSource`/
// `GeoJSONSource.setData` (`GeoJSON.GeoJSON | string`) rather than named as
// the ambient `GeoJSON.FeatureCollection` — this package's tsconfig `types`
// list doesn't expose that global namespace for our own files to name
// directly, only for maplibre-gl's own declarations to resolve internally
// (see the same note in @wehunt/map's geoJsonLayers.tsx).
export interface GeoJsonMultiPolygonFeature {
  type: 'Feature'
  properties: Record<string, unknown>
  geometry: { type: 'MultiPolygon'; coordinates: GeoJsonPos[][][] }
}

export interface GeoJsonLineStringFeature {
  type: 'Feature'
  properties: Record<string, unknown>
  geometry: { type: 'LineString'; coordinates: GeoJsonPos[] }
}

export interface GeoJsonPointFeature {
  type: 'Feature'
  properties: Record<string, unknown>
  geometry: { type: 'Point'; coordinates: GeoJsonPos }
}

export interface GeoJsonFeatureCollection<F> {
  type: 'FeatureCollection'
  features: F[]
}

const toPos = (p: GeoPt): GeoJsonPos => [p.lon, p.lat]

/** A ring closed for GeoJSON (first coordinate repeated at the end). Rings
 *  everywhere else in this package are stored open (see polygonUnion.ts). */
function closedRing(ring: readonly GeoPt[]): GeoJsonPos[] {
  const coords = ring.map(toPos)
  const [fx, fy] = coords[0]
  const [lx, ly] = coords[coords.length - 1]
  return fx === lx && fy === ly ? coords : [...coords, [fx, fy]]
}

/**
 * MultiPolygon features for one ground layer, one feature per merged shape.
 * Properties: `{ key, name, color }` — color resolved per the hunt precedence
 * rule for layer 'hunt', absent otherwise. Rings closed for GeoJSON.
 */
export function groundFeatureCollection(
  areas: readonly GeoArea[] | undefined,
  layer: GroundLayer,
  opts?: { teamColor?: string | null },
): GeoJsonFeatureCollection<GeoJsonMultiPolygonFeature> {
  const shapes = groundShapes(areas, layer)
  const features: GeoJsonMultiPolygonFeature[] = shapes.map((shape) => {
    const polys = ringsToPolygons(shape.rings, shape.outerCount)
    const coordinates: GeoJsonPos[][][] = polys.map((poly) => poly.map(closedRing))
    const properties: Record<string, unknown> = { key: shape.key, name: shape.name }
    if (layer === 'hunt') {
      properties.color = resolveHuntBorderColor({
        areaColor: shape.color,
        teamColor: opts?.teamColor,
      })
    }
    return {
      type: 'Feature',
      properties,
      geometry: { type: 'MultiPolygon', coordinates },
    }
  })
  return { type: 'FeatureCollection', features }
}

/**
 * The wash a border style lays under the ground layers, as one MultiPolygon
 * feature: every oval (reverse) or every filled part (filled), holes included.
 * One feature rather than one per polygon because nothing styles them apart.
 */
export function washFeatureCollection(
  polys: readonly GeoPt[][][],
): GeoJsonFeatureCollection<GeoJsonMultiPolygonFeature> {
  if (polys.length === 0) return { type: 'FeatureCollection', features: [] }
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {},
      geometry: { type: 'MultiPolygon', coordinates: polys.map((poly) => poly.map(closedRing)) },
    }],
  }
}

/** The clamp `lineStrokeWidthCssPx`/`clampLineWidth` apply internally, kept in
 *  sync via the four exported constants rather than re-hardcoded here. An
 *  unset/NaN width falls back to the app's documented default (10), not the
 *  floor of the range — GeoLine.width is "4–24, default 10". */
function clampLineWidth(width: number | undefined): number {
  if (width === undefined || Number.isNaN(width)) return LINE_DEFAULT_WIDTH
  return Math.min(LINE_MAX_WIDTH, Math.max(LINE_MIN_WIDTH, width))
}

/**
 * LineString features for drawn lines/arrows/trails.
 * Properties: `{ id, color, w, kind, style }` where `w` = clampedWidth ×
 * per-kind appearance thickness (the zoom-independent factor of the width
 * law).
 *
 * `id` is what makes a rendered line identifiable again after a tap: the map
 * editor's transparent wide hit layer is fed this very collection, and its
 * click router turns a `queryRenderedFeatures` hit back into a line by reading
 * `properties.id`. Without it the router stringified `undefined`, the editor's
 * lookup for that id missed, and tapping a line on the map silently did
 * nothing while the line list's pencil button — which never goes through a
 * feature — still opened the same editor. The ground (border/sub-area/
 * forbidden) hit features carry `id` for the same reason.
 */
export function lineFeatureCollection(
  lines: readonly GeoLine[] | undefined,
  thickness: { lines: number; arrows: number },
): GeoJsonFeatureCollection<GeoJsonLineStringFeature> {
  const features: GeoJsonLineStringFeature[] = (lines ?? [])
    .filter((line) => line.points.length >= 2)
    .map((line) => {
      const kindThickness = line.kind === 'arrow' ? thickness.arrows : thickness.lines
      const w = clampLineWidth(line.width) * kindThickness
      return {
        type: 'Feature',
        properties: {
          id: line.id,
          color: line.color ?? LINE_DEFAULT_COLOR,
          w,
          kind: line.kind,
          style: line.style ?? 'solid',
        },
        geometry: {
          type: 'LineString',
          coordinates: line.points.map(toPos),
        },
      }
    })
  return { type: 'FeatureCollection', features }
}

export interface HeatPoint {
  id: string
  lat: number
  lon: number
  /** Heat weight: the legacy renderer weighted a downed report 0.55 and an
   *  observation 0.3 — the caller bakes that in. */
  weight: number
}

/** Point features with a `weight` property, for a heatmap layer. */
export function heatFeatureCollection(
  points: readonly HeatPoint[],
): GeoJsonFeatureCollection<GeoJsonPointFeature> {
  const features: GeoJsonPointFeature[] = points.map((point) => ({
    type: 'Feature',
    properties: { id: point.id, weight: point.weight },
    geometry: { type: 'Point', coordinates: [point.lon, point.lat] },
  }))
  return { type: 'FeatureCollection', features }
}

/**
 * The legacy heat look, expressed as MapLibre heatmap paint. Radius:
 * 26·2^(z−12) clamped to [10, 90] — the exponential-2 interpolation stops at
 * (10.62, 10) and (13.79, 90) hit exactly those bounds (26·2^(10.62−12) ≈ 10,
 * 26·2^(13.79−12) ≈ 90), and MapLibre clamps outside the stop range, which IS
 * the legacy clamp. Opacity 0.9 is the legacy alpha cap 230/255.
 */
export function heatmapPaint(): Record<string, unknown> {
  return {
    'heatmap-weight': ['get', 'weight'],
    'heatmap-radius': [
      'interpolate', ['exponential', 2], ['zoom'],
      10.62, 10, 13.79, 90,
    ],
    'heatmap-color': [
      'interpolate', ['linear'], ['heatmap-density'],
      0, 'rgba(255,232,115,0)',
      0.15, '#ffe873',
      0.35, '#ffc93c',
      0.6, '#ff9231',
      0.85, '#f4511e',
      1, '#c62828',
    ],
    'heatmap-opacity': 0.9,
  }
}

/**
 * MapLibre zoom expression implementing the ground width law
 * (2^(0.6z) with the zoom-response floor at GROUND_MIN_EFFECTIVE_ZOOM).
 * Below the first stop MapLibre clamps to the first output — the floor.
 */
export function groundWidthExpression(base: number, thicknessScale: number): unknown {
  return [
    'interpolate', ['exponential', 2 ** 0.6], ['zoom'],
    GROUND_MIN_EFFECTIVE_ZOOM - MAPLIBRE_LAW_ZOOM_OFFSET, groundStrokeCssPx(base, GROUND_MIN_EFFECTIVE_ZOOM, thicknessScale),
    MAP_MAX_ZOOM, groundStrokeCssPx(base, MAP_MAX_ZOOM + MAPLIBRE_LAW_ZOOM_OFFSET, thicknessScale),
  ]
}

/**
 * The `line-offset` that puts a ground's translucent band OUTSIDE the ground —
 * beyond the edge on an outer ring, into the hole on a hole — matching the
 * app's `StrokePosition.outside`. Half the band's width, NEGATED.
 *
 * The negation is the whole point of this existing, so it is worth stating why,
 * because the obvious reasoning gets it backwards. MapLibre offsets a line to
 * its right relative to the direction of travel, and `unionedRings` deliberately
 * hands every ground counter-clockwise outers and clockwise holes — from which
 * it looks like a POSITIVE offset lands outside. It does, but only for a
 * `LineString`. These layers are fed a `MultiPolygon`
 * (`groundFeatureCollection`), and a GeoJSON source tiles its data before the
 * renderer ever sees it: polygon rings are rewound to the vector-tile
 * convention, so the direction `orient()` set never survives to the layer. A
 * measured render settles what reasoning cannot — with an identical ring and an
 * identical positive offset, a `LineString` puts the band outside and a
 * `MultiPolygon` puts it inside.
 *
 * That is how the band came to sit inside the ground on every web surface while
 * the app drew it outside: read as a line-winding question the sign looked
 * right, and no upstream value can be inspected to tell you otherwise.
 *
 * One helper rather than the expression inlined at each layer, because the two
 * surfaces that draw this band (the read-only team map and the editor) are
 * copies of one another, and a sign that disagrees between them is exactly the
 * divergence this band has produced before.
 */
export function groundHaloOffsetExpression(band: number, thicknessScale: number): unknown {
  return groundWidthExpression(-band / 2, thicknessScale)
}

/**
 * The editor's combined ground hit-target width: `GROUND_HIT_TARGET_CSS_PX`
 * flat for sub-area/forbidden, or `max(GROUND_HIT_TARGET_CSS_PX, core width
 * × GROUND_HALO_RATIO)` for a border — so a thick border's hit target never
 * gets HARDER to click than its own visible stroke (mirrors the old canvas's
 * `hitWidth` law in `renderBorderShape`).
 *
 * Same root-must-be-a-single-interpolate constraint as `lineWidthExpression`
 * (see its own doc comment): a real `addLayer` rejects a `["zoom"]`-driven
 * `interpolate`/`step` that isn't the ROOT of the expression tree, even when
 * it's nested a few levels down inside `match`/`max`/`*` wrappers — exactly
 * what this expression used to do (`groundWidthExpression(...)`'s own
 * interpolate wrapped in `['*', GROUND_HALO_RATIO, …]` wrapped in `['max',
 * …]` wrapped in `['match', …]`). `@wehunt/map/testing`'s fake never
 * validates expression shape, so every unit test rendering this layer passed
 * while the real engine's `addLayer` failed validation and never created the
 * layer at all — silently taking every ground click-to-select with it (a
 * `queryRenderedFeatures` call naming a layer id that isn't on the map
 * returns `[]`, not an error the caller sees).
 *
 * The fix is the same shape `lineWidthExpression` already uses: `interpolate`
 * is the root, and the feature/zoom-dependent `match`/`max` moves INSIDE each
 * stop's OUTPUT instead of wrapping the interpolate — the zoom half is
 * resolved to a plain number per stop (`groundStrokeCssPx` at that fixed
 * zoom) before the `match`/`max` ever sees it, so there is only ever the one,
 * top-level, `["zoom"]`-driven interpolate in the whole tree.
 */
export function groundHitWidthExpression(base: number, thicknessScale: number): unknown {
  const borderWidthAt = (zoom: number) =>
    Math.max(GROUND_HIT_TARGET_CSS_PX, GROUND_HALO_RATIO * groundStrokeCssPx(base, zoom, thicknessScale))
  const output = (zoom: number) => ['match', ['get', 'kind'], 'border', borderWidthAt(zoom), GROUND_HIT_TARGET_CSS_PX]
  return [
    'interpolate', ['exponential', 2 ** 0.6], ['zoom'],
    GROUND_MIN_EFFECTIVE_ZOOM - MAPLIBRE_LAW_ZOOM_OFFSET, output(GROUND_MIN_EFFECTIVE_ZOOM),
    MAP_MAX_ZOOM, output(MAP_MAX_ZOOM + MAPLIBRE_LAW_ZOOM_OFFSET),
  ]
}

/**
 * MapLibre zoom expression implementing the line width law (∝ √mapScale, i.e.
 * exponential base √2), multiplied by the per-feature factor `['get', 'w']`
 * (and, for the casing pass, `extraFactor`).
 *
 * The per-unit factor is evaluated at width 10 (`lineStrokeWidthCssPx(10, z) /
 * 10`) — a value inside the clamp range, so dividing it back out gives the
 * exact zoom-only response with the clamp bypassed. The clamped width itself
 * already lives in the `w` feature property (see `lineFeatureCollection`).
 *
 * The multiplication happens INSIDE each `interpolate` stop's output, not by
 * wrapping the whole `interpolate` in an outer `['*', ...]` — the MapLibre
 * style spec only allows a `["zoom"]` expression as the direct input to a
 * top-level `step`/`interpolate`, so `interpolate` has to be the root
 * expression here. This surfaced only against a real MapLibre style
 * validator (`addLayer` on the genuine library) — the `@wehunt/map/testing`
 * fake used by every unit test never validates expression shape, so a
 * `"zoom" expression may only be used as input to a top-level "step" or
 * "interpolate" expression` console error on every drawn-line layer was
 * invisible until this package's e2e suite ran against the real engine.
 */
export function lineWidthExpression(extraFactor = 1): unknown {
  const perUnit = (zoom: number) => lineStrokeWidthCssPx(10, zoom + MAPLIBRE_LAW_ZOOM_OFFSET) / 10
  const output = (zoom: number) =>
    extraFactor === 1
      ? ['*', ['get', 'w'], perUnit(zoom)]
      : ['*', extraFactor, ['get', 'w'], perUnit(zoom)]
  return [
    'interpolate', ['exponential', Math.SQRT2], ['zoom'],
    MAP_MIN_ZOOM, output(MAP_MIN_ZOOM),
    MAP_MAX_ZOOM, output(MAP_MAX_ZOOM),
  ]
}

/** Dash pattern per app line style, in `line-dasharray` units — MapLibre's
 *  are multiples of the LAYER's OWN `line-width`, unlike `mapLines.ts`'s
 *  SVG-era `lineDashArray`, which multiplies a resolved *pixel* width (this
 *  function's the MapLibre twin, not a replacement for that one). The base
 *  multiples are the same app numbers either way: dashed `[6, 3]`, dotted
 *  `[1, 2]`, dash-dot `[6, 2, 1, 2]`. `undefined` for `solid` or an unmapped
 *  style — omitting `line-dasharray` from the paint spec keeps a line solid. */
const DASH_MULTIPLES: Partial<Record<GeoLineStyle, number[]>> = {
  dashed: [6, 3],
  dotted: [1, 2],
  dash_dot: [6, 2, 1, 2],
}

/**
 * `factor` divides the base multiples — required whenever the caller draws
 * this pattern on a layer whose `line-width` is not the app's own per-line
 * width, because a MapLibre dasharray unit is always relative to THAT
 * layer's width. The casing pass is exactly this case: it draws at
 * `LINE_BORDER_FACTOR` (1.1×) the interior's width, so passing the interior's
 * un-divided multiples drew the casing's dash/gap runs 1.1× physically
 * longer than the interior's — the black casing dashes protruding past the
 * coloured ones. Dividing by `factor` here is what makes both layers' dash
 * runs land on the same physical (CSS-pixel) length again.
 */
export function lineDashPattern(style: GeoLineStyle | undefined, factor = 1): number[] | undefined {
  const base = style ? DASH_MULTIPLES[style] : undefined
  if (!base) return undefined
  return base.map((m) => Math.round((m / factor) * 100) / 100)
}

/**
 * Bearing (degrees clockwise from north) of the segment a→b in Web Mercator
 * screen space — the rotation an arrowhead marker at b needs.
 *
 * `mercY` is 0..1 over the world while lon is degrees, so the y-delta is
 * scaled by 360 to put both axes in the same units before `atan2`.
 */
export function segmentBearingDeg(a: GeoPt, b: GeoPt): number {
  return Math.atan2(b.lon - a.lon, (mercY(a.lat) - mercY(b.lat)) * 360) * (180 / Math.PI)
}
